<template>
    <div>
        <div class="row">
            <div class="col-10 col-sm-11 pr-0">
                <app-input label="Group name"
                           :name="'pricing-name-' + pricingId"
                           :id="'pricing-name-' + pricingId"
                           v-validate="'required|max:40'"
                           v-model="name"
                           type="text">
                </app-input>
            </div>
            <div v-if="canRemove"
                 class="col-2 col-sm-1 d-flex align-items-center justify-content-center cursor-pointer mb-2 p-0"
                 @click="onRemove">
                <img src="../../../../assets/img/delete-red.svg">
            </div>
        </div>
        <choice :options="types" v-model="type"></choice>
        <div class="row">
            <div class="col-12 col-xl-6">
                <select-box label="From"
                            :default-value="rangeStartValue"
                            :data-list="rangeList"
                            @selected="onRangeStartSelect">
                </select-box>
            </div>
            <div class="col-12 col-xl-6">
                <select-box label="To"
                            :default-value="rangeEndValue"
                            :data-list="rangeList"
                            @selected="onRangeEndSelect">
                </select-box>
            </div>
        </div>
        <div v-if="!rangeValid && formValid" class="error range-error">
            {{ type }} from should be after {{ type }} to
        </div>
        <div class="row mb-3">
            <div class="col-6">
                <time-picker id="time-from"
                             label="Time from"
                             :default-hours="9"
                             :default-minutes="0"
                             v-model="timeFrom"
                             v-validate="'required|timeBefore:timeTo'">
                </time-picker>
            </div>
            <div class="col-6">
                <time-picker id="time-to"
                             label="To"
                             ref="timeTo"
                             :default-hours="12"
                             :default-minutes="0"
                             v-model="timeTo"
                             v-validate="'required'">
                </time-picker>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import { Confirmation } from '@/store/modules/confirm/Confirmation';
    import { GRADES } from '@/constants/school-grades';
    import { TYPES } from '@/constants/ages-grades';
    import { AGES } from '@/constants/school-ages';

    export default {
        name: 'CampPricing',
        components: {
            SelectBox: () => import('@/components/controls/SelectBox'),
            TimePicker: () => import('../../../controls/TimePicker'),
            AppInput: () => import('../../../controls/AppInput'),
            VueSlider: () => import('vue-slider-component'),
            Choice: () => import('../../../controls/Choice'),
            DatePicker: () => import('../../../controls/DatePicker')
        },
        inject: ['$validator'],
        props: {
            pricingId: {
                type: [Number, String],
                required: false
            },
            pricingName: {
                type: String,
            },
            pricingGradeFrom: {
                type: [String, null],
            },
            pricingGradeTo: {
                type: [String, null],
            },
            pricingAgeFrom: {
                type: [String, null],
            },
            pricingAgeTo: {
                type: [String, null],
            },
            pricingTimeFrom: {
                type: [String, null],
            },
            pricingTimeTo: {
                type: [String, null],
            },
            canRemove: {
                type: Boolean,
                default: true
            },
            formValid: {
                type: Boolean,
                required: true
            }
        },
        data() {
            return {
                GRADES,
                types: TYPES,
            }
        },
        computed: {
            type: {
                get() {
                    return this.gradeFrom || !this.ageFrom ? TYPES[0] : TYPES[1];
                },
                set(value) {
                    if (value === TYPES[0]) {
                        this.gradeFrom = GRADES[0].data;
                        this.gradeTo = GRADES[GRADES.length - 1].data;
                        this.ageFrom = null;
                        this.ageTo = null;
                    } else {
                        this.ageFrom = AGES[0].data;
                        this.ageTo = AGES[AGES.length - 1].data;
                        this.gradeFrom = null;
                        this.gradeTo = null;
                    }
                }
            },
            name: {
                get() {
                    return this.pricingName;
                },
                set(value) {
                    this.$emit('input:name', value);
                }
            },
            gradeFrom: {
                get() {
                    return this.pricingGradeFrom;
                },
                set(value) {
                    this.$emit('input:grade-from', value);
                }
            },
            gradeTo: {
                get() {
                    return this.pricingGradeTo;
                },
                set(value) {
                    this.$emit('input:grade-to', value);
                }
            },
            ageFrom: {
                get() {
                    return this.pricingAgeFrom;
                },
                set(value) {
                    this.$emit('input:age-from', value);
                }
            },
            ageTo: {
                get() {
                    return this.pricingAgeTo;
                },
                set(value) {
                    this.$emit('input:age-to', value);
                }
            },
            timeFrom: {
                get() {
                    return this.pricingTimeFrom;
                },
                set(value) {
                    this.$emit('input:time-from', value);
                }
            },
            timeTo: {
                get() {
                    return this.pricingTimeTo;
                },
                set(value) {
                    this.$emit('input:time-to', value);
                }
            },
            rangeList() {
                return this.type === TYPES[0] ?
                    GRADES.map(grade => grade.view) :
                    AGES.map(age => age.view);
            },
            rangeValid() {
                return this.rangeList.indexOf(this.rangeStartValue) <= this.rangeList.indexOf(this.rangeEndValue);
            },
            rangeStartValue() {
                return this.type === TYPES[0] ?
                    this.gradeFrom && GRADES.find(grade => grade.data === this.gradeFrom).view :
                    this.ageFrom && AGES.find(age => age.data === this.ageFrom).view;
            },
            rangeEndValue() {
                return this.type === TYPES[0] ?
                    this.gradeTo && GRADES.find(grade => grade.data === this.gradeTo).view :
                    this.ageTo && AGES.find(age => age.data === this.ageTo).view;
            },
        },
        watch: {
            rangeValid: {
                handler(value) {
                    this.$emit('range-validate', value);
                },
                immediate: true
            }
        },
        methods: {
            ...mapActions(['openConfirmDialog']),
            onRemove() {
                this.openConfirmDialog(new Confirmation(() => {
                    this.$emit('remove')
                }));
            },
            onRangeStartSelect(data) {
                if (data) {
                    if (this.type === TYPES[0]) {
                        this.gradeFrom = GRADES.find(grade => grade.view === data).data;
                        this.resetAge();
                    } else {
                        this.ageFrom = AGES.find(age => age.view === data).data;
                        this.resetGrade();
                    }
                }
            },
            onRangeEndSelect(data) {
                if (data) {
                    if (this.type === TYPES[0]) {
                        this.gradeTo = GRADES.find(grade => grade.view === data).data;
                        this.resetAge();
                    } else {
                        this.ageTo = AGES.find(age => age.view === data).data;
                        this.resetGrade();
                    }
                }
            },
            resetAge() {
                this.ageFrom = null;
                this.ageTo = null;
            },
            resetGrade() {
                this.gradeFrom = null;
                this.gradeTo = null;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../assets/colors";

    .range {
        margin: 24px 0 28px;
    }

    label {
        margin-bottom: 0;
        font-family: Asap;
        font-size: 14px;
        color: $WhiteGrey;
    }

    .range-error {
        margin-top: -30px;
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/camp-form/CampPricingCreate.vue